/* eslint-disable @typescript-eslint/no-namespace */
import * as L from "leaflet";
import { ImageOverlayOptions, LatLngExpression } from "leaflet";
import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";

import "leaflet-imageoverlay-rotated";

declare module "leaflet" {
  namespace ImageOverlay {
    interface Rotated extends ImageOverlay {
      reposition(
        topleft: LatLngExpression,
        topright: LatLngExpression,
        bottomleft: LatLngExpression
      ): void;
    }
  }

  namespace imageOverlay {
    function rotated(
      imgSrc: string | HTMLImageElement | HTMLCanvasElement,
      topleft: LatLngExpression,
      topright: LatLngExpression,
      bottomleft: LatLngExpression,
      options?: ImageOverlayOptions
    ): ImageOverlay.Rotated;
  }
}

export type RotatedImageOverlayProps = Omit<ImageOverlayOptions, "bounds"> & {
  url: string;
  layerId?: string | number;
  topLeft: LatLngExpression;
  topRight: LatLngExpression;
  bottomLeft: LatLngExpression;
};

export const RotatedImageOverlay = ({
  url,
  topLeft,
  topRight,
  bottomLeft,
  ...props
}: RotatedImageOverlayProps) => {
  const map = useMap();

  const imageOverlayRef = useRef<L.ImageOverlay.Rotated | null>(null);
  const prevOverlay = useRef<string | null>(null);

  const hasOverlayChanged = prevOverlay.current !== url;

  useEffect(() => {
    if (imageOverlayRef.current && !hasOverlayChanged) {
      imageOverlayRef.current.reposition(topLeft, topRight, bottomLeft);
    } else {
      if (imageOverlayRef.current) {
        map.removeLayer(imageOverlayRef.current);
      }

      prevOverlay.current = url;
      imageOverlayRef.current = L.imageOverlay.rotated(url, topLeft, topRight, bottomLeft, {
        ...props,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        pmIgnore: true
      });

      // Adding image overlay sometimes throws an error when map is in edit mode, but other than that adds everything as expected
      try {
        imageOverlayRef.current.addTo(map);
      } catch (e) {
        console.log("Happened when adding image overlay", e);
      }
    }
  }, [map, url, topLeft, topRight, bottomLeft, props, hasOverlayChanged]);

  useEffect(() => {
    return () => {
      if (imageOverlayRef.current) {
        map.removeLayer(imageOverlayRef.current);

        imageOverlayRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};
